import React, { useState, useEffect } from 'react';
import LayoutNavSectionList from 'components/layout_and_quote_builder/LayoutNavSectionList.jsx';
import LayoutContext from 'components/contexts/LayoutContext.jsx';
import QuoteBuilderManagePanel from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderManagePanel.jsx';
import QuoteBuilderForm from 'components/layout_and_quote_builder/quote_builder/QuoteBuilderForm.jsx';
import PresentedDielineOptions from 'components/layout_and_quote_builder/quote_builder/PresentedDielineOptions.jsx';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import MyEventListener from 'components/common/MyEventListener.jsx';
import { CacheProvider } from 'components/layout_and_quote_builder/quote_builder/CacheContext.jsx';
import ErrorBoundary from 'components/common/ErrorBoundary.jsx';
import { readSelectedManufacturingLineList } from 'components/frank/utils/manufacturingLineListUrlState.js';
import { DebugPopupProvider } from 'components/layout_and_quote_builder/DebugPopup.jsx';
import FrankSetupWizard from 'components/frank/FrankSetupWizard.jsx';

// Frank's tabbed front end for the Manufacturing Cost Calculator, served at /mcc. It reuses the same content
// components the legacy stacked page (ShowLayoutAndQuoteBuilderPage) renders -- they already save to the
// user_quote, handle read-only, currency and status -- but lays them out as tabs instead of one long page:
//   Manage (quantity / windows / currency + status) | one tab per nav-builder section (e.g. Carton) |
//   Customer (name / email / phone / notes) | Dieline (material + dimensions).
// The section tabs render LayoutNavSectionList with a single section, so the existing Frank-mode gating
// (fetch Frank's section-material-sheets endpoint, hide the manufacturing lines + solutions) carries over.
const FrankQuoteBuilderPage = ({
  config, navSections, dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale,
  theme, btn_action, dimension_texts_on_layouts_enabled, user_quote_id, dieline_svg, header
}) => {
  const LayoutContextValues = {
    dimensionGroups, layoutOptions, formulaOptions, optionsUserEntered, unit, locale, theme, btn_action,
    dimension_texts_on_layouts_enabled,
  };

  // Manage, then THREE tabs per nav-builder part section (e.g. Carton -> "Carton - Manufacturing Line",
  // "Carton - Solution", "Carton - Pricing"), then Customer and Dieline. The 'general' section is the nav
  // builder's catch-all, not a manufacturing part_type, so it gets no tabs. The three tabs are the three views
  // of the section's ManufacturingLineLists (the listing, the solution cards, the priced view); the user picks
  // a manufacturing line, then a solution, then sees the price.
  const priceableSections = navSections
    .map((section, index) => ({ section, index }))
    .filter(({ section }) => section.part_type !== 'general');

  // The three stage tabs a section is split into, in order. `stage` is the ManufacturingLineLists stageView.
  const sectionStageTabs = [
    { suffix: 'line', stage: 'line', labelKey: 'quote_builder.frank_tab_manufacturing_line' },
    { suffix: 'solution', stage: 'solution', labelKey: 'quote_builder.frank_tab_solution' },
    { suffix: 'pricing', stage: 'pricing', labelKey: 'quote_builder.frank_tab_pricing' },
  ];
  const sectionTabs = priceableSections.flatMap(({ section, index }) => sectionStageTabs.map((stageTab) => ({
    key: `section_${index}_${stageTab.suffix}`,
    label: `${section.label || section.part_type} - ${t(stageTab.labelKey)}`,
    sectionIndex: index,
    stage: stageTab.stage,
  })));
  const tabs = [
    { key: 'manage', label: t('quote_builder.manage') },
    ...sectionTabs,
    { key: 'customer', label: t('quote_builder.customer') },
    { key: 'dieline', label: t('quote_builder.dieline') },
  ];

  const [count, setCount] = useState(1000);
  // Open on the section a refresh / shared link picked a plan in (its manufacturing_line_<index> is in the
  // URL), so the restored selection reopens on its own section. Otherwise the first part section (e.g. Carton)
  // -- the manufacturing costs it lists are what the page is FOR, so it is what a user wants to see first. A
  // quote with no part section (only the nav builder's 'general' catch-all) has no section tab, and falls back
  // to Manage.
  const restoredSection = priceableSections.find(
    ({ index }) => readSelectedManufacturingLineList(index) !== null
  );
  const initialSectionIndex = restoredSection ? restoredSection.index : priceableSections[0]?.index;
  const [activeTab, setActiveTab] = useState(
    initialSectionIndex != null ? `section_${initialSectionIndex}_line` : 'manage'
  );

  // The guided setup wizard auto-opens on a quote that has not been set up yet -- nothing priced -- so a fresh
  // quote is walked through quantity -> pick a line -> pick a solution per section. A quote that already has a
  // priced section opens straight to the page; the "Guided setup" button reopens the wizard any time. While it
  // is open the active tab's section body is not rendered, so the wizard's own ManufacturingLineLists is the
  // only one on screen (two would fight over the manufacturing_line_<index> URL state); closing it re-renders
  // the section, which reads the now-priced state the wizard persisted.
  const anySectionPriced = (window.user_quote_nav_sections || [])
    .some((section) => section.selected_lists && section.selected_lists.length > 0);
  const [wizardOpen, setWizardOpen] = useState(sectionTabs.length > 0 && !anySectionPriced);

  useEffect(() => { window.user_quote_id = user_quote_id; }, []);

  MyEventListener([
    { key: 'quote_builder.count_changed', callback: (msg) => { setCount(() => msg.detail); } }
  ]);

  const readOnly = window.user_quote_finalised;

  const isSectionTab = sectionTabs.some((tab) => tab.key === activeTab);

  const renderActiveTab = () => {
    if (activeTab === 'manage') {
      return (
        <>
          <QuoteBuilderForm config={config.general} readOnly={readOnly} />
          <QuoteBuilderManagePanel readOnly={readOnly} />
        </>
      );
    }
    if (activeTab === 'customer') {
      return <QuoteBuilderForm config={config.customer} readOnly={readOnly} />;
    }
    if (activeTab === 'dieline') {
      // Show the dieline SVG (rendered server-side with its dimension texts) above the material +
      // dimension table, so the user can see the dieline being priced. dieline_svg is inline SVG markup.
      return (
        <>
          {dieline_svg && (
            <div
              style={{ maxWidth: '930px', margin: '0 auto 15px' }}
              dangerouslySetInnerHTML={{ __html: dieline_svg }}
            />
          )}
          <div style={{ maxWidth: '620px', margin: '0 auto' }}>
            <PresentedDielineOptions />
          </div>
        </>
      );
    }

    const sectionTab = sectionTabs.find((tab) => tab.key === activeTab);
    if (sectionTab) {
      // A single-section list, so the section's own Frank-mode material-sheet fetch + gating are reused. The
      // KEY is the section (not the stage tab), so the three stage tabs share ONE ManufacturingLineLists
      // instance -- the picked plan and its solved solutions carry across tabs; only stageView changes.
      // Picking a manufacturing line or a solution stays on its own tab (so the selected one, and the dimmed
      // rest, stay visible); the user moves on with the tabs. Visiting the Pricing tab commits + prices the
      // marked solution.
      return (
        <LayoutNavSectionList
          key={`section_${sectionTab.sectionIndex}`}
          navSections={[navSections[sectionTab.sectionIndex]]}
          count={count}
          config={config}
          stageView={sectionTab.stage}
        />
      );
    }
    return null;
  };

  const tabButtonStyle = (isActive) => ({
    padding: '10px 22px',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: isActive ? 'bold' : 'normal',
    color: isActive ? '#eb5424' : '#555',
    borderBottom: isActive ? '3px solid #eb5424' : '3px solid transparent',
    marginBottom: '-2px',
  });

  return (
    <ErrorBoundary componentName="FrankQuoteBuilderPage" errorContext={{ user_quote_id: user_quote_id }}>
      <CacheProvider initialCache={window.user_quote_section_properties_cached}>
        <LayoutContext.Provider value={LayoutContextValues}>
          <DebugPopupProvider>
          <div className={`${styles.component_container} container`}>
            {/* The tab row: the tabs get the FULL width (the breadcrumb moved below the divider, so the longer
                per-section labels -- "Carton - Manufacturing Line" ... -- are not squeezed), with the "Guided
                setup" button on the right. Aligning to flex-end sits the tab underlines on the shared border. */}
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', borderBottom: '2px solid #ddd' }}>
              <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', alignItems: 'flex-end' }}>
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    style={tabButtonStyle(activeTab === tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              {/* Reopen the guided setup wizard any time; only shown when there is a part section to set up. */}
              {sectionTabs.length > 0 && (
                <div style={{ paddingBottom: '8px', paddingLeft: '12px' }}>
                  <button
                    type="button"
                    className="dct-button dct-button--secondary dct-button--small"
                    onClick={() => setWizardOpen(true)}
                  >
                    {t('setup_wizard.open')}
                  </button>
                </div>
              )}
            </div>

            {/* The page breadcrumb (parent | model | catalog name), under the divider on the top-right -- out of
                the tab row so the tabs use the full width. Right-aligned and with no bottom margin, so it sits in
                the corner and the section content (the filter card) starts right under the tabs. */}
            {header && (
              <div className="page-in-name" style={{ fontSize: '16px', margin: '8px 0 0', textAlign: 'right' }}>
                <span>
                  {header.demo
                    ? header.demoLabel
                    : <a href={header.parentUrl}>{header.parentName}</a>}
                </span>
                {/* The shared `.page-in-name h1` rule forces 23px; override it inline so this breadcrumb sits
                    at the tab-label size. */}
                <h1 style={{ fontSize: 'inherit' }}>
                  {' | '}<a href={header.groupUrl}>{header.groupName}</a>
                  {' | '}<a href={header.catalogUrl}>{header.catalogName}</a>
                </h1>
              </div>
            )}

            {/* A FORM tab is constrained to a readable column, centered, so its labels sit near their inputs
                rather than stretching label-left / input-right across the whole page. A SECTION tab is not:
                it lists the manufacturing line cards, which are sized as a fraction of this container (4 to a
                row, like the legacy listing) -- in a 620px column each card is ~139px and its machine columns
                are crushed to ~60px. So the section tabs get the container's full width. */}
            {/* The active tab's body is hidden while the wizard is open, so only the wizard's own
                ManufacturingLineLists is mounted (two would fight over the manufacturing_line_<index> URL
                state). Closing the wizard re-renders it, now reading the priced state the wizard persisted. */}
            {!wizardOpen && (
              <div style={isSectionTab || activeTab === 'dieline' ? undefined : { maxWidth: '620px', margin: '0 auto' }}>
                {renderActiveTab()}
              </div>
            )}
          </div>

          {wizardOpen && (
            <FrankSetupWizard
              navSections={navSections}
              config={config}
              onClose={() => setWizardOpen(false)}
            />
          )}
          </DebugPopupProvider>
        </LayoutContext.Provider>
      </CacheProvider>
    </ErrorBoundary>
  );
};

export default FrankQuoteBuilderPage;
